import React from 'react';
import { useQuery } from 'react-query';
import { leadsAPI } from '../../services/api';
import { formatDateTimeInViewerTZ, getViewerTimezone } from '../../utils/dateTimeUtils';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Chip,
} from '@mui/material';

const LeadTimeline = ({ leadId }) => {
  const { user } = useAuth();
  const viewerTimezone = getViewerTimezone(user?.timezone);
  const { data, isLoading, error } = useQuery(['lead-timeline', leadId], () =>
    leadsAPI.getTimeline(leadId).then((res) => res.data)
  );

  if (!leadId) return null;

  if (isLoading) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">Failed to load timeline</Typography>
      </Box>
    );
  }

  const timeline = data?.data?.timeline || [];
  const statusHistory = data?.data?.status_history || [];

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Activity Timeline
      </Typography>
      {timeline.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No activity recorded yet.
        </Typography>
      )}
      {timeline.map((item) => (
        <Paper key={item.id} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
          <Typography fontWeight={600}>{item.activity_description}</Typography>
          <Typography variant="body2" color="text.secondary">
            {item.performed_by_name || 'Unknown'} • {formatDateTimeInViewerTZ(item.created_at, viewerTimezone)}
          </Typography>
          {item.metadata && (
            <Typography variant="caption" color="text.secondary">
              {JSON.stringify(item.metadata)}
            </Typography>
          )}
        </Paper>
      ))}
      {statusHistory.length > 0 && (
        <>
          <Typography variant="subtitle1" sx={{ mt: 2 }}>
            Status History
          </Typography>
          {statusHistory.map((sh) => (
            <Box key={sh.id} sx={{ mb: 1 }}>
              <Chip label={`${sh.old_status || 'N/A'} → ${sh.new_status}`} size="small" sx={{ mr: 1 }} />
              <Typography variant="caption" color="text.secondary">
                {sh.changed_by_name || 'Unknown'} @ {formatDateTimeInViewerTZ(sh.created_at, viewerTimezone)}
              </Typography>
            </Box>
          ))}
        </>
      )}
    </Paper>
  );
};

export default LeadTimeline;
